import React, { useEffect, useState, useCallback } from "react";
import Typography from "@mui/material/Typography";
import {
  Box,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Stack,
  FormControl,
  Select,
  type SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import "./DashboardView.css";
import { type ParsedReport, ReportExecutionResult, ReportToParsedReport } from "../Reports/ReportList";
import { getDashboardConfig, getReportLang, getReports, getStoredOptions, StoredOption } from "../../api/data/reportToolsDrf";
import { useCustomer } from "../../context/CustomerContext";
import MiniReport from "../Reports/MiniReport2";
import { dataToExcel, getExportData } from "../../api/tools/dataToExcel";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  format as formatDateFns,
  subDays as subDaysFns,
  addDays as addDaysFns,
  subDays,
  addDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
} from "date-fns";
import ButtonA from "../../components/Shared/ButtonA";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { getDasboardReport } from "../../globals_VITE";
import { t } from "i18next";
import { fillPlaceholders } from "../Reports/reportTools";
import i18n from "../../i18n";

const DATE_FORMAT_PARAM = "YYYY-MM-DD"; // Изменено на формат, совместимый с Dayjs

const REPORT_ID_Pivot = getDasboardReport('pivot'); // 34; // 2; // Відвідувачі Pivot
const REPORT_ID_22 = 28; // Відвідувачі
const REPORT_ID_23 = getDasboardReport('sex'); // 33; // 27; // Відвідувачі - Стать
const REPORT_ID_24 = getDasboardReport('age');  // 32; // 26; // Відвідувачі - Вік

const EXPORT_TABLE_NAME = "pcnt.v_export_vca";

// Тип периода, выбираемого пользователем
type UserPeriod = "DAY" | "WEEK" | "MONTH" | "YEAR" | "PERIOD";

type ReportParameter = {
  name: string;
  value: string | number | boolean;
};

// Helper для инициализации дат
const getInitialDates = (
  userPeriod: UserPeriod
): { startDate: Date; endDate: Date, userPeriod: UserPeriod } => {
  const today = new Date();
  const d2 = subDaysFns(today, 1); // Вчера как конечная дата для всех режимов по умолчанию
  let d1: Date;

  switch (userPeriod) {
    case "DAY":
      d1 = d2;
      break;
    case "WEEK":
      d1 = subDaysFns(d2, 6);
      break;
    case "MONTH":
      d1 = subDaysFns(d2, 29);
      break;
    case "YEAR":
      d1 = subDaysFns(d2, 364);
      break;
    default: // Fallback на месяц
      d1 = subDaysFns(d2, 29);
  }
  return { startDate: d1, endDate: d2, userPeriod};
};

// --- Компонента выбора периода через меню ---
// Перенесено за межі DashboardView для запобігання повторному створенню при кожному рендері.
const PeriodMenu: React.FC<{
  anchorEl: null | HTMLElement;
  open: boolean;
  onClose: () => void;
  onSelect: (userPeriod: UserPeriod) => void;
  activePeriod: UserPeriod;
}> = ({ anchorEl, open, onClose, onSelect, activePeriod }) => (
  <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
    <MenuItem
      selected={activePeriod === "DAY"}
      onClick={() => {
        onSelect("DAY");
        onClose();
      }}
    >
      {t('PeriodMenu.DAY')}
    </MenuItem>
    <MenuItem
      selected={activePeriod === "WEEK"}
      onClick={() => {
        onSelect("WEEK");
        onClose();
      }}
    >
      {t('PeriodMenu.WEEK')}
    </MenuItem>
    <MenuItem
      selected={activePeriod === "MONTH"}
      onClick={() => {
        onSelect("MONTH");
        onClose();
      }}
    >
      {t('PeriodMenu.MONTH')}
    </MenuItem>
    <MenuItem
      selected={activePeriod === "YEAR"}
      onClick={() => {
        onSelect("YEAR");
        onClose();
      }}
    >
      {t('PeriodMenu.YEAR')}
    </MenuItem>
    <MenuItem
      selected={activePeriod === "PERIOD"}
      onClick={() => {
        onSelect("PERIOD");
        onClose(); 
      }}
    >
      {t('PeriodMenu.PERIOD')}
    </MenuItem>
  </Menu>
);

// --- Компонента выбора диапазона дат или одной даты ---
// Перенесено за межі DashboardView для запобігання повторному створенню при кожному рендері.
const DateRangeOrSinglePicker: React.FC<{
  userPeriod: UserPeriod;
  value: [Dayjs, Dayjs] | Dayjs;
  onChange: (value: [Dayjs, Dayjs] | Dayjs) => void;
}> = ({ userPeriod, value, onChange }) => {
  console.log("userPeriod, value", userPeriod, value);
  if (userPeriod === "DAY") {
    return (
      <DatePicker
        label="Оберіть дату"
        value={value as Dayjs}
        onChange={(date) => date && onChange(date)}
        slotProps={{ textField: { size: "small" } }}
      />
    );
  }
  // Для 'PERIOD' и других диапазонов
  // --- ЗАЩИТНАЯ ПРОВЕРКА ---
  // Предотвращает сбой из-за рассинхронизации состояний. Если компонент ожидает
  // массив дат (для периода), а получает одиночную дату, мы логируем ошибку
  // и ничего не рендерим, вместо того чтобы "упасть".
  if (!Array.isArray(value)) {
    console.error(
      "Inconsistent state in DateRangeOrSinglePicker: Received a single date value for a range period.",
      { userPeriod, value }
    );
    return null; // Предотвращаем падение приложения
  }

  const [start, end] = value; // Теперь эта строка безопасна
  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <DatePicker
        label={t('DateRangeOrSinglePicker.Start')}
        value={start}
        onChange={(date) => date && onChange([date, end])}
        slotProps={{ textField: { size: "small" } }}
      />
      <DatePicker
        label={t('DateRangeOrSinglePicker.Finish')}
        value={end}
        onChange={(date) => date && onChange([start, date])}
        slotProps={{ textField: { size: "small" } }}
      />
    </Box>
  );
};

export interface DashboardConfig {
  pivot: number;
  gender: number;
  age: number;
}

interface DashboardViewProps {
  pointsVL: StoredOption[];
  dashboardConfig: DashboardConfig;
}

const DashboardView: React.FC<DashboardViewProps> = ({pointsVL, dashboardConfig}) => {
  
  // console.log('pointsVL', pointsVL);
  
  const { customerData, isLoading: isCustomerLoading } = useCustomer();
  const [_parsedReport22, setParsedReport22] = useState<ParsedReport | null>(null);
  const [parsedReport23, setParsedReport23] = useState<ParsedReport | null>(null);
  const [parsedReport24, setParsedReport24] = useState<ParsedReport | null>(null);
  const [parsedReportPivot, setParsedReportPivot] = useState<ParsedReport | null>(null);

  // Состояние для периода, выбранного пользователем
  const [activePeriod, setActivePeriod] = useState<UserPeriod>("MONTH");

  const [reportParams, setReportParams] = useState<ReportParameter[]>(() => {
    const { startDate, endDate, userPeriod } = getInitialDates("MONTH"); // Инициализация для 'MONTH'
    return [
      { name: "d1", value: dayjs(startDate).format(DATE_FORMAT_PARAM) }, // Форматируем с Dayjs
      { name: "d2", value: dayjs(endDate).format(DATE_FORMAT_PARAM) }, // Форматируем с Dayjs
      { name: "mode", value: userPeriod },
      { name: "point", value: -1 }, // Initial default point, will be updated by useEffect
    ];
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isDatePickerModalOpen, setDatePickerModalOpen] = useState(false);

  // Временное состояние для дат в модальном окне
  const [tempDateValue, setTempDateValue] = useState<[Dayjs, Dayjs] | Dayjs>(
    () => {
      const d1 = dayjs(reportParams.find((p) => p.name === "d1")?.value as string);
      if (activePeriod === "DAY") {
        return d1;
      }
      const d2 = dayjs(reportParams.find((p) => p.name === "d2")?.value as string);
      return [d1, d2];
    }
  );

  const handleOpenDatePickerModal = useCallback(() => {
    // Инициализируем временное состояние текущими датами из reportParams
    // useCallback с зависимостями гарантирует, что мы всегда читаем
    const d1 = dayjs(
      reportParams.find((p) => p.name === "d1")?.value as string
    );
    if (activePeriod === "DAY") {
      setTempDateValue(d1);
    } else {
      const d2 = dayjs(
        reportParams.find((p) => p.name === "d2")?.value as string
      );
      setTempDateValue([d1, d2]);
    }
    setDatePickerModalOpen(true);
  }, [
    reportParams,
    activePeriod,
    setTempDateValue,
    setDatePickerModalOpen,
  ]);

  const handleCloseDatePickerModal = () => setDatePickerModalOpen(false);

  const handleApplyDateChange = () => {
    const isDay = activePeriod === "DAY";
    const d1 = isDay
      ? (tempDateValue as Dayjs)
      : (tempDateValue as [Dayjs, Dayjs])[0];
    const d2 = isDay
      ? (tempDateValue as Dayjs)
      : (tempDateValue as [Dayjs, Dayjs])[1];

    handleParamsUpdateFromControl([
      // d1.format() уже использует Dayjs, теперь с правильным форматом
      { name: "d1", value: d1.format(DATE_FORMAT_PARAM) },
      { name: "d2", value: d2.format(DATE_FORMAT_PARAM) },
      { name: "mode", value: isDay ? "DAY" : "PERIOD" },
    ]);
    handleCloseDatePickerModal();
  };

  // Effect to set initial 'point' in reportParams based on customerData
  useEffect(() => {
    let newPointValue: number = -1; // Default

    if (!isCustomerLoading) {
      // Only act if customer data loading is complete
      if (
        customerData &&
        customerData.points &&
        customerData.points.length > 0
      ) {
        newPointValue = customerData.points[0].value;
      }
      // If customerData is loaded but no points, newPointValue remains -1.
    } else {
      // While customer data is loading, do nothing to avoid premature changes.
      return;
    }

    setReportParams((prevParams) => {
      const currentPointValueInParams = prevParams.find(
        (p) => p.name === "point"
      )?.value;
      if (currentPointValueInParams !== newPointValue) {
        return prevParams.map((p) =>
          p.name === "point" ? { ...p, value: newPointValue } : p
        );
      }
      return prevParams; // No change needed
    });
  }, [customerData, isCustomerLoading, setReportParams]);


  useEffect(() => {
    const loadSingleReport = async (rid: number): Promise<ParsedReport> => {
      const fetchedReports = await getReportLang(rid, i18n.language || 'uk');
      if (!fetchedReports || fetchedReports.length === 0) {
        throw new Error(`Звіт з ID ${rid} не знайдено.`);
      }
      return ReportToParsedReport(fetchedReports[0]);
    };

    const loadAllReports = async () => {
      setIsLoading(true);
      setFetchError(null);
      try {
        const results = await Promise.allSettled([
          loadSingleReport(REPORT_ID_22),
          loadSingleReport(dashboardConfig.gender), // REPORT_ID_23
          loadSingleReport(dashboardConfig.age), // REPORT_ID_24
          loadSingleReport(dashboardConfig.pivot), // REPORT_ID_Pivot
        ]);
        let firstError: string | null = null;
        const handleResult = (
          result: PromiseSettledResult<ParsedReport>,
          setter: React.Dispatch<React.SetStateAction<ParsedReport | null>>,
          reportId: number
        ) => {
          if (result.status === "fulfilled") {
            setter(result.value);
          } else {
            const errorMsg = `Помилка завантаження звіту ${reportId}: ${
              result.reason?.message || result.reason
            }`;
            console.error(errorMsg, result.reason);
            if (!firstError) firstError = errorMsg;
          }
        };
        handleResult(results[0], setParsedReport22, REPORT_ID_22);
        handleResult(results[1], setParsedReport23, dashboardConfig.gender); // REPORT_ID_23);
        handleResult(results[2], setParsedReport24, dashboardConfig.age); // REPORT_ID_24);
        handleResult(results[3], setParsedReportPivot, dashboardConfig.pivot); // REPORT_ID_Pivot);

        if (firstError) {
          setFetchError(firstError);
        }
      } catch (err: any) {
        console.error("Непередбачена помилка під час завантаження звітів:",err);
        setFetchError(err.message || "Сталася невідома помилка");
      } finally {
        setIsLoading(false);
      }
    };
    loadAllReports();
  }, []);

  // Обработчик смены гранулярности пользователем
  const handlePeriodChange = useCallback(
    (newPeriod: UserPeriod) => {
      setActivePeriod(newPeriod);
      const { startDate, endDate, userPeriod } = getInitialDates(newPeriod);

      // СИНХРОНИЗАЦИЯ: Немедленно обновляем tempDateValue, чтобы его тип
      // (одиночная дата или массив) соответствовал новой гранулярности.
      // Это предотвращает ошибку "value is not iterable", когда компонент
      // DateRangeOrSinglePicker перерисовывается с новой гранулярностью,
      // но со старым значением tempDateValue.
      if (newPeriod === "DAY") {
        setTempDateValue(dayjs(startDate));
      } else {
        setTempDateValue([dayjs(startDate), dayjs(endDate)]);
      }

      setReportParams((prevParams) => {
        const existingPointParam = prevParams.find(
          (p) => p.name === "point"
        ) || { name: "point", value: -1 };
        return [
          { name: "d1", value: dayjs(startDate).format(DATE_FORMAT_PARAM) }, // Форматируем с Dayjs
          { name: "d2", value: dayjs(endDate).format(DATE_FORMAT_PARAM) }, // Форматируем с Dayjs
          { name: "mode", value: userPeriod },
          existingPointParam, // Preserve existing point
        ];
      });
      if (newPeriod === "PERIOD") {
        setDatePickerModalOpen(true);
      }
    },
    [setActivePeriod, setReportParams, setTempDateValue]
  );

  // Обработчик обновления параметров от дочерних контролов (ReportDayControl, ReportPeriodControl)
  const handleParamsUpdateFromControl = useCallback(
    (dateAndModeParams: ReportParameter[]) => {
      // Assuming dateAndModeParams contains {name: 'd1', ...}, {name: 'd2', ...}, {name: 'mode', ...}
      setReportParams((prevParams) => {
        const pointParam = prevParams.find((p) => p.name === "point") || {
          name: "point",
          value: -1,
        };

        // Create a map from the new date/mode params for easy lookup
        const newParamsMap = new Map(dateAndModeParams.map((p) => [p.name, p]));

        // Build the new parameters array, prioritizing new date/mode, keeping existing point and others
        const resultParams: ReportParameter[] = prevParams
          .filter((p) => p.name !== "point" && !newParamsMap.has(p.name)) // Keep other old params
          .concat(dateAndModeParams) // Add new date/mode params
          .concat([pointParam]); // Add the point param

        // Deduplicate (just in case, ensuring pointParam is the one used for 'point')
        const finalMap = new Map(resultParams.map((p) => [p.name, p]));
        finalMap.set("point", pointParam); // Ensure our point is authoritative
        return Array.from(finalMap.values());
      });
    },
    [setReportParams]
  );

  const handlePointChange = useCallback(
    (event: SelectChangeEvent<string | number>) => {
      const newPointId =
        typeof event.target.value === "string"
          ? parseInt(event.target.value, 10)
          : event.target.value;
      setReportParams((prevParams) =>
        prevParams.map((p) =>
          p.name === "point" ? { ...p, value: newPointId } : p
        )
      );
    },
    [setReportParams]
  );

  const handleExport = async () => {
    setIsExporting(true);
    // const filename = `export_${EXPORT_TABLE_NAME}`;
    // добавил в название экспортируемого файла наименование, если point выбран
    const point_id = reportParams.find((p) => p.name === "point")?.value || 0;
    const _pointLabel =
      customerData?.points?.find((p) => p.value === point_id)?.label ||
      "всі_пункти";
    const filename = `export_point_${_pointLabel}`;
    try {
      const data0 = await getExportData(
        point_id === -1 ? undefined : { point_id }
      );
      const data1 = data0.map((item: any) => ({
        age: item.age,
        date: item.date,
        point: item.point,
        gender: item.gender,
      }));
      dataToExcel(data1, filename);
      console.log("Export successful");
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const currentPointSelected =
    (reportParams.find((p) => p.name === "point")?.value as number) ?? -1;

  console.log("customerData.points", customerData?.points);

  // --- Обработчики пролистывания периодов ---
  const handlePrevPeriod = useCallback(() => {
    const d1 = new Date(
      reportParams.find((p) => p.name === "d1")?.value as string
    );
    const d2 = new Date(
      reportParams.find((p) => p.name === "d2")?.value as string
    );

    let newD1 = d1,
      newD2 = d2;
    switch (activePeriod) {
      case "DAY":
        newD1 = subDays(d1, 1);
        newD2 = subDays(d2, 1);
        break;
      case "WEEK":
        newD1 = startOfWeek(subDays(d1, 1));
        newD2 = endOfWeek(subDays(d1, 1));
        break;
      case "MONTH":
        newD1 = startOfMonth(subDays(d1, 1));
        newD2 = endOfMonth(subDays(d1, 1));
        break;
      case "YEAR":
        newD1 = startOfYear(subDays(d1, 1));
        newD2 = endOfYear(subDays(d1, 1));
        break;
    }
    handleParamsUpdateFromControl([
      // Форматируем с Dayjs
      { name: "d1", value: dayjs(newD1).format(DATE_FORMAT_PARAM) },
      { name: "d2", value: dayjs(newD2).format(DATE_FORMAT_PARAM) },
      // { name: "mode", value: activePeriod === "DAY" ? "DAY" : "PERIOD" },
      { name: "mode", value: activePeriod },
    ]);
  }, [reportParams, activePeriod, handleParamsUpdateFromControl]);

  const handleNextPeriod = useCallback(() => {
    const d1 = new Date(
      reportParams.find((p) => p.name === "d1")?.value as string
    );
    const d2 = new Date(
      reportParams.find((p) => p.name === "d2")?.value as string
    );

    let newD1 = d1,
      newD2 = d2;
    switch (activePeriod) {
      case "DAY":
        newD1 = addDays(d1, 1);
        newD2 = addDays(d2, 1);
        break;
      case "WEEK":
        newD1 = startOfWeek(addDays(d2, 1));
        newD2 = endOfWeek(addDays(d2, 1));
        // newD1 = addDays(d1, 7);
        // newD2 = addDays(d2, 7);
        break;
      case "MONTH":
        newD1 = startOfMonth(addDays(d2, 1));
        newD2 = endOfMonth(addDays(d2, 1));
        break;
      case "YEAR":
        newD1 = startOfYear(addDays(d2, 1));
        newD2 = endOfYear(addDays(d2, 1));
        break;
    }
    handleParamsUpdateFromControl([
      // Форматируем с Dayjs
      { name: "d1", value: dayjs(newD1).format(DATE_FORMAT_PARAM) },
      { name: "d2", value: dayjs(newD2).format(DATE_FORMAT_PARAM) },
      // { name: "mode", value: activePeriod === "DAY" ? "DAY" : "PERIOD" },
      { name: "mode", value: activePeriod },
    ]);
  }, [reportParams, activePeriod, handleParamsUpdateFromControl]);

  // --- Состояния для меню выбора периода ---
  const [periodMenuAnchor, setPeriodMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const openPeriodMenu = Boolean(periodMenuAnchor);
  const handleOpenPeriodMenu = (event: React.MouseEvent<HTMLElement>) =>
    setPeriodMenuAnchor(event.currentTarget);
  const handleClosePeriodMenu = () => setPeriodMenuAnchor(null);

  const handleDateRangeChange = (
    startDate: dayjs.Dayjs | null,
    endDate: dayjs.Dayjs | null
  ) => {
    console.log("Выбранная дата начала:", startDate?.format("YYYY-MM-DD")); // Используем .format() для Dayjs
    console.log("Выбранная дата окончания:", endDate?.format("YYYY-MM-DD"));

    // Здесь вы можете обновить состояние родительского компонента,
    // отправить данные на сервер, отфильтровать список и т.д.
  };

  const d1String = formatDateFns(
    new Date(reportParams.find((p) => p.name === "d1")?.value as string),
    "dd.MM.yyyy"
  );
  const d2String = formatDateFns(
    new Date(reportParams.find((p) => p.name === "d2")?.value as string),
    "dd.MM.yyyy"
  );
  const displayDate =
    activePeriod === "DAY" ? d1String : `${d1String} - ${d2String}`;
  console.log("displayDate", displayDate);

  const reportParamsPivot = reportParams;
  // костыль
  // const reportParamsPivot = reportParams.map(param => {
  //     if (param.name === 'point') {
  //         return { ...param, value: -1 }; // Создаем новый объект с обновленным значением
  //     }
  //     return param; // Возвращаем неизмененный объект
  // });

  const renderMenuItems = () => {
    // const allPoints =
    //   customerData?.points?.length == 0
    //     ? []
    //     : [{ value: -1, label: t("All") }, ...(customerData?.points ?? [])];
    const allPoints =
      pointsVL.length == 0
        ? []
        : [{ value: -1, label: t("All") }, ...(pointsVL)];
    return allPoints.map((point) => (
      <MenuItem key={point.value} value={point.value}>
        {point.label}
      </MenuItem>
    ));
  };

  const hideEmptyResult = (result: ReportExecutionResult) => {
    // single column indicates error
    if (result.columns.length == 1)
      return true;

    // two-column report (['gender',rate]) is empty when sum(rate)==0
    if (result.columns.length == 2) {
      const total = result.rows.reduce((s, r) => s + +Number(r[1]), 0);
      return total == 0;
    }
    return false;
  }

  // костыль
  const onTitleRender = (report: ParsedReport, parameters: { name: string; value: string | number | boolean }[]) => {
    const paramsMap = Object.fromEntries(parameters.map(p => [p.name, p.value]));
    const { d1, d2 } = paramsMap;
    const title = (d1 && d2 && d1 === d2) ? report.name1 : report.name;
    return fillPlaceholders(title, parameters);
  };

  const onTitleRender_ = (
      report: ParsedReport, 
      parameters: { name: string; value: string | number | boolean }[]
  ) => {
    const d1 = parameters.find(p => p.name === 'd1')?.value;
    const d2 = parameters.find(p => p.name === 'd2')?.value;

    if (d1 !== undefined && d1 === d2) {
        return `Кількість відвідувачів за ${d1}`;
    } else {
      return fillPlaceholders(report.name, parameters)
        // return `Кількість відвідувачів з ${d1} по ${d2}`;
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        id={"22222222"}
        sx={{
          padding: 0,
          flexGrow: 1,
          backgroundImage: "url(/bg1.png)",
          backgroundRepeat: "repeat",
          backgroundSize: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "1300px",
            margin: "0 auto",
            padding: 1,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>
                Завантаження даних звітів...
              </Typography>
            </Box>
          )}
          {fetchError && !isLoading && (
            <Alert severity="error" sx={{ my: 2 }}>
              {fetchError}
            </Alert>
          )}

          {!isLoading && !fetchError && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                my: 2,
                alignItems: "center",
                width: "100%",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: "space-between",
              }}
            >
              {/* Селектор пункта слева */}
              <Box
                sx={{
                  flexShrink: 0,
                  minWidth: { xs: "100%", sm: 200, md: 240 },
                }}
              >
                <FormControl fullWidth size="small" variant="outlined">
                  {/* No InputLabel as per requirement */}
                  <Select
                    value={currentPointSelected}
                    onChange={handlePointChange}
                    inputProps={{ "aria-label": "Оберіть пункт" }}
                    sx={{ backgroundColor: "white" }}
                    disabled={isCustomerLoading}
                  >
                    {isCustomerLoading ? (
                      <MenuItem value={currentPointSelected} disabled>
                        <em>Завантаження пунктів...</em>
                      </MenuItem>
                    ) : customerData?.points &&
                      customerData.points.length > 0 ? (
                      renderMenuItems()
                    ) : (
                      // (
                      //     customerData.points.map((point) => (
                      //         <MenuItem key={point.value} value={point.value}>
                      //             {point.label}
                      //         </MenuItem>
                      //     ))
                      // ) : (
                      <MenuItem value={-1}>
                        <em>{"нема даних"}</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
              {/* Кнопки управления справа */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  flexGrow: 1,
                }}
              >
                <Tooltip title="Гортати на попередній період">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handlePrevPeriod}
                    >
                      <ArrowBackIosNewIcon fontSize="small" />
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Гортати на наступний період">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleNextPeriod}
                    >
                      <ArrowForwardIosIcon fontSize="small" />
                    </Button>
                  </span>
                </Tooltip>
                {/* <Button variant="outlined" size="small" onClick={handleOpenDatePickerModal}>
                      {displayDate}
                  </Button> */}
                <Tooltip 
                  title="Вибір періоду" 
                  placement="top-start"
                  PopperProps={{
                    sx: {
                      zIndex: 1000, // Пример: понизить z-index тултипа
                    },
                  }}
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleOpenPeriodMenu}
                    >
                      <ArrowDropDownIcon fontSize="small" />
                    </Button>
                    <PeriodMenu
                      anchorEl={periodMenuAnchor}
                      open={openPeriodMenu}
                      onClose={handleClosePeriodMenu}
                      onSelect={handlePeriodChange}
                      // onOpenDatePickerModal больше не передается, так как вызов синхронный
                      activePeriod={activePeriod}
                    />
                  </span>
                </Tooltip>
                <Tooltip title={t('DashboardView.export')}>
                  <span>
                    <ButtonA
                      variant="contained"
                      color="secondary"
                      onClick={handleExport}
                      isActive={isExporting}
                      startIcon={<FileDownloadIcon />}
                      hideTextOn="md"
                      size="small"
                    >
                      {t('DashboardView.export')}
                    </ButtonA>
                  </span>
                </Tooltip>
              </Box>
            </Stack>
          )}

          {!isLoading && !fetchError && (
            <>
              <Grid container rowSpacing={1} sx={{ my: 2, width: "100%" }}>
                {parsedReportPivot && (
                  <Grid item xs={12} md={12}>
                    <Box sx={{ px: 1.5, pb: 1 }}>
                      <MiniReport
                        report={parsedReportPivot}
                        parameters={reportParamsPivot}
                        displayMode="pivot"
                        height="450px"
                        onTitleRender={onTitleRender}
                      />
                    </Box>
                  </Grid>
                )}
                {parsedReport24 && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ px: 1.5, pb: 1 }}>
                      <MiniReport
                        report={parsedReport24}
                        parameters={reportParams}
                        displayMode="chart"
                        height="300px"
                        hideEmptyResult={hideEmptyResult}
                      />
                    </Box>
                  </Grid>
                )}
                {parsedReport23 && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ px: 1.5, pb: 1 }}>
                      <MiniReport
                        report={parsedReport23}
                        parameters={reportParams}
                        displayMode="chart"
                        height="300px"
                        hideEmptyResult={hideEmptyResult}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Box>
        <Dialog
          open={isDatePickerModalOpen}
          onClose={handleCloseDatePickerModal}
          maxWidth="md"
        >
          <DialogTitle>{t('DashboardView.Select_date_or_period')}</DialogTitle>
          <DialogContent>
            <Box
              sx={{
                pt: 2,
                minWidth: { md: 500 },
                display: "flex",
                justifyContent: "center",
              }}
            >
              {/* Условный рендеринг: компонент монтируется только при открытии диалога,
                                когда все состояния уже синхронизированы. Это предотвращает ошибку. */}
              {isDatePickerModalOpen && (
                <DateRangeOrSinglePicker
                  userPeriod={activePeriod}
                  value={tempDateValue}
                  onChange={setTempDateValue}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDatePickerModal}>Скасувати</Button>
            <Button onClick={handleApplyDateChange} variant="contained">
              Застосувати
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardView;
